import { computed } from 'vue'
import { freezeObject, isFunction } from '@/global/utils/utils'
import { api } from '@/global/services/api'
import { registeredTableHandlers } from '@/global/components/view-layouts/tabs-layout/config/registered-table-handlers'
import { showDetailsAction, deleteAction } from '@/global/components/table/helpers/tableActions'

const TABLE_KEY = 'product-categories'
const UI_VIEW = 'temporary_regulations_product_categories'

export function useTable (options) {
  const {
    trans,
    openProductCategoryDialogFn,
    openInfoDialogFn,
    closeInfoDialogFn
  } = options || {}

  const fetchTableData = () => {
    const { fetchData } = registeredTableHandlers?.[TABLE_KEY] || {}
    if (!isFunction(fetchData)) {
      return
    }
    fetchData()
  }

  const onCreateNewProductCategory = () => {
    openProductCategoryDialogFn({
      title: trans('road-maintenance/work_orders.adding_new_product_category'),
      editId: null,
      description: null,
      useSerialNumber: false
    })
  }

  const onShowProductCategoryDetails = (event) => {
    const {
      id,
      description,
      use_serial_number: useSerialNumber
    } = event?.data || {}
    const descriptionTitle = description?.length > 20
      ? description.substring(0, 20) + '...'
      : description || ''

    openProductCategoryDialogFn({
      title: trans('road-maintenance/work_orders.updating_product_category') + (descriptionTitle ? ' - ' + descriptionTitle : ''),
      editId: id,
      description,
      useSerialNumber: !!useSerialNumber
    })
  }

  const deleteProductCategory = async (event) => {
    try {
      const { id } = event?.data || {}
      if (!id) {
        return
      }

      await api('road-maintenance').delete(`product-types/${id}`)
      closeInfoDialogFn()
      fetchTableData()
    }
    catch (exception) {}
  }

  const onProductCategoryDelete = (event) => {
    const { description } = event?.data || {}
    const descriptionTitle = description?.length > 20
      ? description.substring(0, 20) + '...'
      : description || ''

    openInfoDialogFn({
      title: trans('road-maintenance/work_orders.product_category_deletion') + (descriptionTitle ? ' - ' + descriptionTitle : ''),
      message: trans('road-maintenance/work_orders.product_category_deletion_message'),
      onConfirm: () => deleteProductCategory(event)
    })
  }

  const apiConfig = computed(() => ({
    get: {
      module: 'road-maintenance',
      route: 'product-types'
    }
  }))

  const cellsConfig = freezeObject({
    use_serial_number: {
      custom_display_value: (data) => {
        return data?.use_serial_number ? trans('base.yes') : trans('base.no')
      }
    }
  })

  const headers = computed(() => ([
    {
      label: trans('road-maintenance/settings.id'),
      key: 'id',
      always_visible: true
    },
    {
      label: trans('road-maintenance/excavation-dropdown-type.name'),
      key: 'description',
      always_visible: true
    },
    {
      label: trans('road-maintenance/work_orders.using_serial_number'),
      key: 'use_serial_number',
      always_visible: true
    }
  ]))

  const actionsConfig = computed(() => ([
    showDetailsAction(trans, onShowProductCategoryDetails),
    deleteAction(trans, onProductCategoryDelete)
  ]))

  const filtersConfig = computed(() => ({
    fields: [
      {
        key: 'description',
        label: trans('road-maintenance/excavation-dropdown-type.name')
      }
    ]
  }))

  const paginationConfig = freezeObject({
    perPageOptions: [50, 100],
    perPage: 50,
    currentPage: 1
  })

  const breadcrumbs = computed(() => ({
    items: [
      {
        text: trans('road-maintenance.settings')
      },
      {
        text: trans('road-maintenance/work_orders.product_categories'),
        bolded: true
      }
    ]
  }))

  const config = computed(() => ({
    tableConfig: {
      key: TABLE_KEY,
      uiView: UI_VIEW,
      apiConfig: apiConfig.value,
      filters_and_columns_visibility_config: {
        newBtnPosition: 'start',
        newClick: onCreateNewProductCategory
      },
      rowDblClick: onShowProductCategoryDetails,
      tableConfig: {
        cellsConfig,
        headers: headers.value,
        actionsConfig: actionsConfig.value,
        filtersConfig: filtersConfig.value,
        paginationConfig
      }
    },
    breadcrumbs: breadcrumbs.value
  }))

  return {
    config,
    fetchTableData
  }
}
